import { useDebounceDeepNull } from 'hooks/useDebounce';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSWRConfig } from 'swr';
import useSWRInfinite from 'swr/infinite';
import { getSrchFilterKey, IfSrchV3Page, SRCH_V3_URL } from './srch-v3-infinite-util';
import { getSrchV3ReqBffRaw, IfSrchV3Req } from './srch-v3-model';
//import { getSrchV3ReqRaw, IfSrchV3Req } from './srch-v3-model';

type KeySuffix = string | number | boolean;

export const useSrchV3InfiniteBase = <T>(
  show: boolean,
  req: IfSrchV3Req,
  fetcher: (key: unknown) => Promise<IfSrchV3Page<T>>,
  keySuffix?: KeySuffix
): { list: T[]; totalCount: number; isLoading: boolean; mutate: () => void } => {
  const debounceReq = useDebounceDeepNull(show ? req : null, 500);

  const getKey = useCallback(
    (pageIndex: number, prev: IfSrchV3Page<T> | null) => {
      if (!debounceReq || !show) return null;
      const isTotalSrch = debounceReq.index === 'TOTAL' || !debounceReq.index;
      const pageSize = isTotalSrch ? debounceReq.totalsize : debounceReq.indexsize;
      const prevItems = prev?.items;
      if (prevItems && prevItems.length < pageSize) return null;
      const reqRaw = getSrchV3ReqBffRaw(debounceReq);
      const key: unknown[] = [SRCH_V3_URL, { ...reqRaw, page: pageIndex + 1 }];
      if (keySuffix !== undefined) key.push(keySuffix);
      return key;
    },
    [debounceReq, show, keySuffix]
  );

  const {
    data,
    mutate: mutateInfinite,
    setSize,
    isLoading,
  } = useSWRInfinite<IfSrchV3Page<T>>(getKey, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 200,
  });

  const list = useMemo(() => (data || []).flatMap((page) => page.items), [data]);
  const totalCount = useMemo(() => data?.[0]?.totalCount ?? 0, [data]);
  const { mutate: mutateSWR } = useSWRConfig();
  const lastSizeRef = useRef({ filterKey: '', page: 0 });

  useEffect(() => {
    if (!show) {
      lastSizeRef.current = { filterKey: '', page: 0 };
    }
  }, [show]);

  useEffect(() => {
    if (!show || !debounceReq) return;
    const filterKey = getSrchFilterKey(debounceReq);
    const page = debounceReq.page ?? 1;
    const last = lastSizeRef.current;
    if (last.filterKey !== filterKey) {
      lastSizeRef.current = { filterKey, page: 1 };
      setSize(1);
      return;
    }
    if (last.page !== page) {
      lastSizeRef.current = { filterKey, page };
      setSize(page);
    }
  }, [show, debounceReq, setSize]);

  const isWaiting = !debounceReq;

  const isSrchV3Key = (key: unknown): boolean => {
    if (typeof key === 'string') {
      return key.startsWith(SRCH_V3_URL);
    }

    if (Array.isArray(key)) {
      return typeof key[0] === 'string' && key[0] === SRCH_V3_URL;
    }

    return false;
  };
  const mutate = useCallback(() => {
    mutateInfinite();
    mutateSWR((key) => isSrchV3Key(key));
  }, [mutateInfinite, mutateSWR]);

  return {
    isLoading: isLoading || isWaiting,
    list,
    mutate,
    totalCount,
  };
};
